import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Import skill icons
import pyimage from "../../assets/images/python.png";
import jsimage from "../../assets/images/js.png";
import tsimage from "../../assets/images/ts.png";
import taltimg from "../../assets/images/tallwind.png";
import botimg from "../../assets/images/boot.png";
import htmltimg from "../../assets/images/html.png";
import csstimg from "../../assets/images/csss.png";
import reactimg from "../../assets/images/rect.png";
import fastapiimg from "../../assets/images/fastapi.png";
import mongodb from "../../assets/images/mongo.png";
import postgress from "../../assets/images/post.png";
import langchain from "../../assets/images/langchain.jpeg";
import langgraph from "../../assets/images/langgraph-color.png";
import openaisdk from "../../assets/images/OpenAI-Logo-PNG.png";

const skills = [
  { name: 'Python', icon: pyimage },
  { name: 'LangChain', icon: langchain },
  { name: 'LangGraph', icon: langgraph },
  { name: 'OpenAI SDK', icon: openaisdk },
  { name: 'JavaScript', icon: jsimage },
  { name: 'TypeScript', icon: tsimage },
  { name: 'Tailwind CSS', icon: taltimg },
  { name: 'Bootstrap', icon: botimg },
  { name: 'HTML', icon: htmltimg },
  { name: 'CSS', icon: csstimg },
  { name: 'React', icon: reactimg },
  { name: 'FastAPI', icon: fastapiimg },
  { name: 'MongoDB', icon: mongodb },
  { name: 'PostgreSQL', icon: postgress },
];

// small descriptive blurbs for the tooltip shown on hover/focus
const skillDetails = {
  Python: 'Scripting, automation, data processing and backend services.',
  LangChain: 'Chain orchestration for LLM apps and prompt management.',
  LangGraph: 'Visual pipelines and graph-based LLM workflows.',
  'OpenAI SDK': 'Working with OpenAI APIs and embeddings.',
  JavaScript: 'Frontend logic, DOM manipulation and tooling.',
  TypeScript: 'Typed JavaScript for safer, scalable code.',
  'Tailwind CSS': 'Utility-first styling and responsive design.',
  Bootstrap: 'Quick responsive layouts and components.',
  HTML: 'Semantic markup and accessible structure.',
  CSS: 'Styling, layout and responsive techniques.',
  React: 'Component-based UI architecture and hooks.',
  FastAPI: 'High-performance Python APIs and async endpoints.',
  MongoDB: 'Document DB for flexible schemas and fast prototyping.',
  PostgreSQL: 'Relational DB with strong consistency and SQL features.',
};

function Skills() {
  const [hoveredSkill, setHoveredSkill] = useState(null);

  const marqueeVariants = {
    animate: { x: ['0%', '-50%'] },
  };

  return (
    <div id="skill" className="w-full min-h-screen bg-gradient-to-b from-gray-900 to-slate-900 flex flex-col justify-center items-center py-12 px-6 overflow-hidden relative">
      {/* Decorative gradient blobs */}
      <div className="absolute -left-24 -top-24 w-72 h-72 rounded-full bg-gradient-to-br from-indigo-600 to-cyan-400 opacity-20 blur-3xl pointer-events-none" />
      <div className="absolute -right-28 bottom-8 w-80 h-80 rounded-full bg-gradient-to-tr from-pink-500 to-amber-400 opacity-12 blur-3xl pointer-events-none" />

      <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4 text-center drop-shadow-md">
        My Technical Skills
      </h2>

      <p className="text-md md:text-lg text-gray-300 text-center max-w-2xl px-4 mb-8 leading-relaxed">
        Passionate about crafting efficient solutions with modern technologies. Continuously learning and building
        responsive, maintainable and high-performance applications.
      </p>

      <div className="w-full overflow-hidden max-w-6xl relative px-2 py-6">
        <motion.div
          className="flex gap-6 items-center"
          variants={marqueeVariants}
          initial={{ x: 0 }}
          animate="animate"
          transition={{ repeat: Infinity, duration: 28, ease: 'linear' }}
        >
          {[...skills, ...skills].map((skill, index) => (
            <motion.div
              key={`${skill.name}-${index}`}
              onMouseEnter={() => setHoveredSkill(skill.name)}
              onMouseLeave={() => setHoveredSkill(null)}
              onFocus={() => setHoveredSkill(skill.name)}
              onBlur={() => setHoveredSkill(null)}
              whileHover={{ scale: 1.06, y: -6, rotate: -1 }}
              whileTap={{ scale: 0.98 }}
              className="flex-shrink-0 p-2 outline-none"
            >
              <div
                tabIndex={0}
                role="button"
                aria-label={`${skill.name} skill card`}
                className={`
                  w-36 h-36 sm:w-40 sm:h-40 md:w-44 md:h-44 lg:w-48 lg:h-48
                  bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm
                  border border-white/10 rounded-2xl
                  flex flex-col items-center justify-center
                  relative overflow-hidden shadow-lg transition-transform duration-300 focus:ring-2 focus:ring-blue-400
                  ${hoveredSkill === skill.name ? 'ring-2 ring-blue-400/40 transform translate-y-0 shadow-2xl scale-105' : ''}
                `}
              >
                {/* subtle shine */}
                <div className={`absolute left-0 top-0 w-full h-1/2 bg-white/10 blur-xl opacity-0 transition-opacity duration-400 ${hoveredSkill === skill.name ? 'opacity-70' : ''}`} />

                <img src={skill.icon} alt={skill.name} className="w-14 h-14 sm:w-16 sm:h-16 object-contain relative z-10" />
                <span className="mt-3 text-gray-100 text-sm font-semibold relative z-10">{skill.name}</span>

                {/* Tooltip / detail blurb */}
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={hoveredSkill === skill.name ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute bottom-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-xs text-gray-200 pointer-events-none max-w-[13rem] text-center"
                >
                  {skillDetails[skill.name] || skill.name}
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* soft edge fades so marquee looks like it's emerging from sides */}
      <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-900 to-transparent z-10 pointer-events-none" />
    </div>
  );
}

export default Skills;
